import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../../../context/AuthProvider'
import { apiRequest } from '../../../lib/api'
import { Button } from '../../../components/ui/button'
import { ArrowLeft, Loader2, Calendar } from 'lucide-react'
import EventCard from '../../../components/events/EventCard'

export default function ParishEvents() {
  const { parishId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(1)
  const [meta, setMeta] = useState(null)
  const limit = 12

  const fetchEvents = async (pageNum) => {
    try {
      setLoading(true)
      setError(null)
      const params = new URLSearchParams({
        page: pageNum,
        limit,
        isPublished: 'true',
      })
      const response = await apiRequest(
        `/api/v1/parishes/${parishId}/events?${params.toString()}`,
      )
      setEvents(response.data || [])
      setMeta(response.meta || null)
    } catch (err) {
      console.error('Failed to fetch events:', err)
      setError(err.message || 'Failed to load events')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (parishId) {
      fetchEvents(page)
    }
  }, [parishId, page])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Button
          variant="ghost"
          onClick={() => navigate(`/app/donor/parish/${parishId}`)}
          className="text-charcoal hover:text-charcoal-muted mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Parish
        </Button>
        <h1 className="text-3xl font-bold font-display text-charcoal">Parish Events</h1>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-gold animate-spin" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded p-6">
          <p className="text-red-600">{error}</p>
        </div>
      ) : events.length === 0 ? (
        <div className="bg-white border border-surface-border rounded p-8 text-center">
          <Calendar className="w-12 h-12 text-charcoal-faint mx-auto mb-3" />
          <p className="text-charcoal-muted">No events found for this parish.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                onClick={() =>
                  navigate(`/app/donor/parish/${parishId}/events/${event.id}`)
                }
              />
            ))}
          </div>

          {/* Pagination */}
          {meta && (meta.hasNextPage || page > 1) && (
            <div className="flex items-center justify-center gap-4 pt-4">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <span className="text-sm text-charcoal-muted">
                Page {page}{meta.totalPages ? ` of ${meta.totalPages}` : ''}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={!meta.hasNextPage}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
